import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLocale, formatDate } from "@/i18n/format";
import { useAuth } from "@/hooks/use-auth";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { ManageUserBrandsDialog } from "@/components/admin/ManageUserBrandsDialog";
import { toast } from "sonner";

interface AdminUser {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  isAllowed: boolean;
  createdAt: string;
}

export function AdminUsersPage() {
  const { t } = useTranslation("admin");
  const locale = useLocale();
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [fetching, setFetching] = useState(true);
  const [managingUser, setManagingUser] = useState<AdminUser | null>(null);

  useEffect(() => {
    if (isLoading) return;
    if (!user?.isSuperAdmin) {
      navigate("/dashboard", { replace: true });
      return;
    }
    fetchUsers();
  }, [isLoading, user]);

  async function fetchUsers() {
    try {
      const res = await fetch("/api/admin/users", { credentials: "include" });
      const json = await res.json();
      setUsers(json.data ?? []);
    } catch {
      toast.error(t("users.toast.loadError"));
    } finally {
      setFetching(false);
    }
  }

  async function toggleAllowed(userId: string, isAllowed: boolean) {
    const prev = users;
    setUsers((u) =>
      u.map((x) => (x.id === userId ? { ...x, isAllowed } : x))
    );

    try {
      const res = await fetch(`/api/admin/users/${userId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isAllowed }),
      });
      if (!res.ok) throw new Error("Failed");
      toast.success(isAllowed ? t("users.toast.approved") : t("users.toast.revoked"));
    } catch {
      setUsers(prev);
      toast.error(t("users.toast.updateError"));
    }
  }

  if (isLoading || fetching) {
    return (
      <div className="p-8">
        <p className="text-muted-foreground text-sm">{t("users.loading")}</p>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-3xl">
      <h1 className="text-xl font-semibold text-foreground mb-1">{t("users.title")}</h1>
      <p className="text-sm text-muted-foreground mb-6">
        {t("users.subtitle")}
      </p>

      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-0 border-b border-border">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">{t("users.table.user")}</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">{t("users.table.joined")}</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">{t("users.table.brands")}</th>
              <th className="text-right px-4 py-3 font-medium text-muted-foreground">{t("users.table.allowed")}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u, i) => (
              <tr
                key={u.id}
                className={i % 2 === 0 ? "bg-surface-1" : "bg-surface-0"}
              >
                <td className="px-4 py-3">
                  <div className="font-medium text-foreground">{u.name}</div>
                  <div className="text-muted-foreground text-xs">{u.email}</div>
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {formatDate(new Date(u.createdAt), locale, { dateStyle: "medium" })}
                </td>
                <td className="px-4 py-3">
                  <Button size="sm" variant="outline" onClick={() => setManagingUser(u)}>
                    {t("users.manage")}
                  </Button>
                </td>
                <td className="px-4 py-3 text-right">
                  <Switch
                    checked={u.isAllowed}
                    onCheckedChange={(checked) => toggleAllowed(u.id, checked)}
                  />
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                  {t("users.noUsers")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ManageUserBrandsDialog
        open={managingUser !== null}
        onOpenChange={(o) => !o && setManagingUser(null)}
        userId={managingUser?.id ?? null}
        userEmail={managingUser?.email ?? ""}
      />
    </div>
  );
}
